import { ApolloLink, Observable, Operation } from "@apollo/client"
import {
  getMainDefinition,
  ObservableSubscription
} from "@apollo/client/utilities"
import { PW_AUTH_TOKEN_KEY } from "@pathwright/auth/src/api/client"
import { getSentAuthToken } from "./refreshedAuthTokenLink"

const isQuery = (operation: Operation) => {
  const definition = getMainDefinition(operation.query)
  return (
    definition.kind === "OperationDefinition" &&
    definition.operation === "query"
  )
}

// Resend a query whose stored auth token changed while it was in flight.
// Apollo deduplicates in-flight queries by document and variables only, so a
// refetch issued right after sign-in or sign-out can share a request that was
// sent with the previous token. Must sit before the auth link so a resend
// picks up the current token.
export const currentAuthTokenLink = new ApolloLink((operation, forward) => {
  if (!isQuery(operation)) return forward(operation)

  return new Observable((observer) => {
    let subscription: ObservableSubscription | undefined

    const send = () => {
      let superseded = false
      subscription = forward(operation).subscribe({
        next: (result) => {
          if (superseded) return
          // The server may reissue the token on this very response, which keeps
          // the same session rather than changing auth.
          const reissued = result.extensions?.refreshedAuthToken !== undefined
          if (
            !reissued &&
            getSentAuthToken(operation) !==
              localStorage.getItem(PW_AUTH_TOKEN_KEY)
          ) {
            superseded = true
            subscription?.unsubscribe()
            send()
            return
          }
          observer.next(result)
        },
        error: (error) => !superseded && observer.error(error),
        complete: () => !superseded && observer.complete()
      })
    }

    send()
    return () => subscription?.unsubscribe()
  })
})
